import { useEffect } from "react";
import { useStore } from "store";
import { useSearchParams } from "react-router-dom";
import { useWindowDimensions } from "resources/BrowserManager";
import { Typography, Space, message, Flex, Card, Tag, Tooltip } from "antd";
const { Title, Text } = Typography;

import ApiManager from "resources/ApiManager/api";
import { getFormattedUnixDate } from "shared/utilities.js";

import CONSTANTS from "shared/constants";
import strings from "resources/strings";

import "./style.scss";
const styleWrapperClass = "cuenta";

import ModalAccountDelete from "./ModalAccountDelete";
import ModalApplyCoupon from "./ModalApplyCoupon";
import ModalPaymentMethod from "./ModalPaymentMethod";

import { TextLine } from "routes/Profile/components/index.js";
import { FormattedAmount } from "routes/Cuenta/components/ModalPaymentMethod.js";
import { PlanTag } from "components/Shared/RfcCard/index.js";

import { ArrowRightOutlined, InfoCircleFilled } from "@ant-design/icons";

export const SubscriptionCard = ({ type, buttonOnly = false }) => {
  const { isSmallView } = useWindowDimensions();

  const user = useStore((state) => state.user);

  return buttonOnly ? (
    <ModalPaymentMethod interactionType={"paymentMethodSetup"} />
  ) : (
    <Card>
      <Flex
        justify="space-between"
        align={isSmallView ? null : "center"}
        gap={isSmallView ? 10 : 30}
        vertical={isSmallView}
      >
        <Flex vertical gap={10}>
          <Title level={4} style={{ marginTop: "0px", marginBottom: "0px" }}>
            {type === "onboarding" ? "Método de pago" : "Subscripción"}
          </Title>

          {user.stripe.subscription ? (
            <Flex vertical gap={5}>
              <Flex vertical gap={10} style={{ marginBottom: "5px" }}>
                {user.stripe.subscription?.items.map((item, index) => (
                  <Flex key={index} align="center" justify="flex-start" gap={5}>
                    <PlanTag planName={item.plan_name} />
                    <Flex style={{ marginLeft: "-5px" }} gap={5}>
                      <Text>{item.quantity}</Text>
                      <Text>x</Text>
                    </Flex>
                    <FormattedAmount amountCents={item.unit_amount} />
                    <ArrowRightOutlined />
                    <FormattedAmount
                      amountCents={item.unit_amount * item.quantity}
                    />
                    <Flex style={{ marginLeft: "-5px" }}>
                      {item.recurring_interval === "month" ? (
                        <Text>/mes</Text>
                      ) : (
                        <Text>/año</Text>
                      )}
                    </Flex>
                  </Flex>
                ))}
              </Flex>
              <TextLine
                label="Total de la subscripción"
                values={[
                  <FormattedAmount
                    amountCents={user.stripe.subscription?.items.reduce(
                      (total, item) => total + item.unit_amount * item.quantity,
                      0,
                    )}
                    currency={
                      user.stripe.subscription?.upcoming_invoice?.currency
                    }
                  />,
                  "/mes",
                ]}
                wrap
              />

              {user.stripe.subscription?.upcoming_invoice && (
                <>
                  <TextLine
                    label="Próximo pago"
                    values={[
                      getFormattedUnixDate(
                        user.stripe.subscription?.upcoming_invoice.period_end,
                        "date",
                      ),
                    ]}
                  />
                  <TextLine
                    label="Monto a pagar"
                    values={[
                      <FormattedAmount
                        amountCents={
                          user.stripe.subscription?.upcoming_invoice.amount_due
                        }
                        currency={
                          user.stripe.subscription?.upcoming_invoice.currency
                        }
                      />,
                      <Tooltip title="El monto a pagar incluye descuentos por créditos disponibles o si tu próximo ciclo de facturación incluye cambios en tu subscripción.">
                        <InfoCircleFilled
                          key="icon-help"
                          className="cursor-help"
                          style={{
                            color: "purple",
                            cursor: "help",
                            marginLeft: "5px",
                          }}
                        />
                      </Tooltip>,
                    ]}
                  />
                  <Text italic>
                    Para cancelar o actualizar tu subscripción, visita la
                    sección de planes en cada RFC.
                  </Text>
                </>
              )}

              {user.stripe.subscription?.cancel_at && (
                <TextLine
                  label="Fecha de cancelación"
                  values={[
                    getFormattedUnixDate(
                      user.stripe.subscription?.cancel_at,
                      "date",
                    ),
                    <Tooltip title="La subscripción se cancelará en la fecha indicada y ya no se realizará ningún cargo después de esa fecha.">
                      <InfoCircleFilled
                        key="icon-help"
                        className="cursor-help"
                        style={{
                          color: "purple",
                          cursor: "help",
                          marginLeft: "5px",
                        }}
                      />
                    </Tooltip>,
                  ]}
                />
              )}
            </Flex>
          ) : (
            <Text>
              No tienes una subscripción activa. Escoge un plan para tus RFCs
              desde la sección de planes en cada RFC.
            </Text>
          )}
        </Flex>
        <ModalPaymentMethod interactionType={"paymentMethodSetup"} />
      </Flex>
    </Card>
  );
};

export default function Cuenta() {
  const { isSmallView } = useWindowDimensions();

  const [searchParams] = useSearchParams();
  const paymentMethodResult = searchParams.get("r");

  const token = useStore((state) => state.token);
  const user = useStore((state) => state.user);
  const setUser = useStore((state) => state.setUser);

  useEffect(() => {
    async function fetchData() {
      const userRes = await getUser(token);
      if (userRes === -1) {
        message.error(strings.alertUnexpectedError);
      } else {
        setUser(userRes);
      }
    }

    fetchData();

    if (paymentMethodResult === "success") {
      message.success("Método de pago actualizado.");
    }
  }, []);

  async function getUser() {
    //Get updated user
    const apiRes = await ApiManager.getUser("invoice_preview");

    //Silently return when error was for session expired, as the api handler will handle the message and redirection to /login
    if (apiRes.response.code === CONSTANTS.API_RESPONSE_ERROR_SESSION_EXPIRED) {
      return;
    }

    if (apiRes.response.code === -1 || apiRes.response.code !== 2000) {
      message.error(strings.alertUnexpectedError);
      return;
    }

    const { user } = apiRes.data;
    return user;
  }

  const deletionDescription =
    "Al eliminar tu cuenta, se cancelarán todos tus planes de subscripción y se borrará toda la información registrada y relacionada a tus RFCs, incluyendo Certificados y CFDIs.";

  const creditsAmount = (
    <Space>
      <Text style={{ fontWeight: "bold", fontSize: "20px" }}>
        <span style={{ whiteSpace: "nowrap" }}>
          ${" "}
          {(user.stripe.credit_balance_cents / 100).toLocaleString("en-US", {
            minimumFractionDigits: 2,
          })}
        </span>
      </Text>
      <Tag color="blue" style={{ fontWeight: "normal" }}>
        MXN
      </Tag>
    </Space>
  );

  return (
    <div className={styleWrapperClass}>
      <Title level={1}>Cuenta</Title>

      <Flex vertical gap={30}>
        <Card>
          <Space direction="vertical" size={0}>
            <Title level={4} className="subtitle">
              Correo electrónico
            </Title>
            <Text>{user.email}</Text>
          </Space>
        </Card>

        <SubscriptionCard />

        <Card>
          {isSmallView ? (
            <Flex vertical gap={5}>
              <Flex justify="space-between">
                <Title level={4} className="subtitle">
                  Créditos
                </Title>
                {creditsAmount}
              </Flex>
              <Space direction="vertical">
                <Text>
                  Tus créditos se acumulan como saldo a favor al cambiar tu RFC
                  de plan o como recompensas por el programa de referidos. El
                  costo de tu subscripción se cubrirá con este saldo.
                </Text>
                <Text strong italic style={{ fontSize: "12px" }}>
                  Los créditos son representativos del saldo a favor del usuario
                  para cubrir gastos de subscripciones dentro del portal. Este
                  saldo no es transferible ni reembolsable.
                </Text>
              </Space>
              <ModalApplyCoupon openTitle={"Aplicar cupón"} />
            </Flex>
          ) : (
            <Flex justify="space-between" gap={30}>
              <Space direction="vertical" size={10}>
                <Flex align="center" justify="flex-start" gap={15}>
                  <Title level={4} className="subtitle">
                    Créditos
                  </Title>
                  <ModalApplyCoupon openTitle={"Aplicar cupón"} />
                </Flex>

                <Space direction="vertical">
                  <Text>
                    Tus créditos se acumulan como saldo a favor al cambiar tu
                    RFC de plan o como recompensas por el programa de referidos.
                    El costo de tu subscripción se cubrirá con este saldo.
                  </Text>
                  <Text strong italic style={{ fontSize: "12px" }}>
                    Los créditos son representativos del saldo a favor del
                    usuario para cubrir gastos de subscripciones dentro del
                    portal. Este saldo no es transferible ni reembolsable.
                  </Text>
                </Space>
              </Space>
              {creditsAmount}
            </Flex>
          )}
        </Card>

        {!CONSTANTS.PROD && ( //TODO: enable account delete for prod once flow is complete
          <Card>
            <Flex
              justify="space-between"
              align={isSmallView ? null : "center"}
              gap={isSmallView ? 10 : 30}
              vertical={isSmallView}
            >
              <Space direction="vertical" size={0}>
                <Title level={4} className="subtitle">
                  Eliminar cuenta
                </Title>
                <Text>{deletionDescription}</Text>
              </Space>

              <ModalAccountDelete
                openTitle={"Eliminar cuenta"}
                description={deletionDescription}
              />
            </Flex>
          </Card>
        )}
      </Flex>
    </div>
  );
}
